import { PropsWithChildren, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { datadogRum } from '@datadog/browser-rum';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';

import { useMetricsContext } from 'src/helpers/MetricsContext';
import { isOpendoorAppEvoWebView } from 'src/helpers/webview';
import {
  useGetHumanQuery,
  useGetSellerInputsQuery,
  useGetSellerInputUuidQuery,
} from 'src/store/api';
import { useAppSelector } from 'src/store/hooks';
import { getInApp, getLeadToken } from 'src/store/router/selectors';
import { handleException } from 'src/store/sellerFlow/actions';
import { updateSellerInputHumanDetails } from 'src/store/sellerInput';

import LoadingSection from '../components/common/loader/LoadingSection';
import { isInExclusiveOffersTreatment } from '../store/sellerFlow/selectors';
import TrackAnswerChange from './TrackAnswerChanges';
import TrackPageChange from './TrackPageChange';

/**
 * This component is responsible for loading all the data needed
 * to initialize our state and kick off the onboarding app.
 *
 * It currently does the following things:
 * - If a web token is given, look up the seller input uuid and use that
 * - Load the related human session
 * - Load the seller input uuid
 * - Update seller input with human info
 */
export const ReceptionLoader: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const urlToken = useAppSelector(getLeadToken);
  const flowInitialized = useAppSelector((state) => state.ui.flowInitialized);

  const { updateContextualOptions } = useMetricsContext();
  const channel = useAppSelector((state) => state.sellerInput.channel);
  const leadState = useAppSelector((state) => state.sellerInput.leadState);
  const market = useAppSelector((state) => state.sellerInput.market ?? '');
  const inApp = useAppSelector(getInApp);

  const shouldLookupUuid = urlToken && urlToken.length == 13;

  const { data: uuid, error: uuidError } = useGetSellerInputUuidQuery(urlToken, {
    skip: !shouldLookupUuid,
  });

  useEffect(() => {
    if (uuidError) {
      dispatch(handleException(uuidError, 'invalid-lead-token', 'fatal', { leadToken: urlToken }));
    }
  }, [dispatch, uuidError, urlToken]);

  const token = shouldLookupUuid ? uuid : urlToken;

  const { data: human, error: humanError } = useGetHumanQuery();

  useEffect(() => {
    if (humanError) {
      dispatch(handleException(humanError, 'fetch-human', 'warn'));
    }
  }, [dispatch, humanError]);

  // setup data dog once we have a token
  useEffect(() => {
    if (token) {
      datadogRum.setUser({
        id: token,
      });
    }
  }, [token]);

  const { data, error } = useGetSellerInputsQuery(token, { skip: !token });
  useEffect(() => {
    if (error) {
      const leadIsNotFound = (errorData: string | { message: string }) => {
        const msg = typeof errorData == 'string' ? errorData : errorData.message;
        return ['NOT_FOUND', 'malformed UUID'].some((partial) => msg.indexOf(partial) !== -1);
      };

      if ('data' in error && leadIsNotFound(error.data)) {
        //redirect to address-entry
        //window.location.href = addressEn();
        dispatch(handleException(error, 'lead-not-found', 'fatal', { leadToken: token }));
        return;
      }

      dispatch(handleException(error, 'fetch-lead', 'fatal', { leadToken: token }));
    }
  }, [dispatch, error, token]);

  // if we have a human and we have seller inputs, update the answer prefills
  useEffect(() => {
    if (data && human) {
      dispatch(
        updateSellerInputHumanDetails({
          uuid: human.customer_uuid ?? undefined,
          email: human.email ?? undefined,
          name: human.full_name ?? undefined,
          phoneNumber: human.primary_phone ?? undefined,
        }),
      );
    }
  }, [data, human, dispatch]);

  const inExclusiveOffersTreatment = useAppSelector(isInExclusiveOffersTreatment);

  const sellerInputUuid = data?.reception?.getSellerInput?.sellerInput?.uuid;

  // update datadog rum context
  useEffect(() => {
    if (sellerInputUuid)
      updateContextualOptions({
        channel: channel,
        leadState: leadState,
        leadToken: sellerInputUuid,
        market: market,
        inApp: inApp,
        shouldSeeExclusiveOffersOnboardingPage: inExclusiveOffersTreatment,
        isMobileNativeWebView: isOpendoorAppEvoWebView,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerInputUuid]);

  if (sellerInputUuid && flowInitialized) {
    return (
      <>
        <TrackPageChange />
        <TrackAnswerChange />
        {inExclusiveOffersTreatment ? (
          <ThemedLite theme="eero">{children}</ThemedLite>
        ) : (
          //eslint-disable-next-line react/jsx-no-useless-fragment
          <>{children}</>
        )}
      </>
    );
  }

  return <LoadingSection />;
};
